import React, { useState, useRef, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ApiLoader from "../common/loader";
import { routes } from "../routings";
import axios from "axios";
import { REACT_APP_API_KEY, REACT_APP_API_URL } from "../common/config";
import { toast } from "react-toastify";

// Validation schema with sanitization
const validationSchema = Yup.object({
  // Authorized or Interested Person Information
  pucName: Yup.string()
    .trim()
    .required("Printed name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters")
    .matches(/^[a-zA-Z\s\-'.]+$/, "Name can only contain letters, spaces, hyphens, apostrophes, and periods"),
  
  pucEmail: Yup.string()
    .trim()
    .required("Email address is required")
    .email("Invalid email address")
    .max(100, "Email must be less than 100 characters"),
  
  pucAddress: Yup.string()
    .trim()
    .required("Address is required")
    .min(5, "Address must be at least 5 characters")
    .max(200, "Address must be less than 200 characters"),
  
  pucCity: Yup.string()
    .trim()
    .required("City is required")
    .min(2, "City must be at least 2 characters")
    .max(100, "City must be less than 100 characters")
    .matches(/^[a-zA-Z\s\-'.]+$/, "City can only contain letters, spaces, hyphens, apostrophes, and periods"),
  
  pucState: Yup.string()
    .trim()
    .required("State is required")
    .min(2, "State must be at least 2 characters")
    .max(50, "State must be less than 50 characters")
    .matches(/^[a-zA-Z\s]+$/, "State can only contain letters and spaces"),
  
  pucPhone: Yup.string()
    .trim()
    .required("Telephone number is required")
    .matches(/^[\d\s\-().+]+$/, "Invalid phone number format")
    .min(10, "Phone number must be at least 10 digits")
    .max(20, "Phone number must be less than 20 characters"),
  
  pucLicNum: Yup.string()
    .trim()
    .required("Driver's license number is required")
    .min(5, "License number must be at least 5 characters")
    .max(30, "License number must be less than 30 characters")
    .matches(/^[a-zA-Z0-9\-\s]+$/, "License number can only contain letters, numbers, hyphens, and spaces"),
  
  // Immobilized Vehicle Information
  pucVehYear: Yup.string()
    .trim()
    .required("Vehicle year is required")
    .matches(/^\d{2,4}$/, "Year must be 2-4 digits")
    .test('valid-year', 'Invalid year', function(value) {
      if (!value) return false;
      const year = parseInt(value);
      const currentYear = new Date().getFullYear();
      // Handle 2-digit years
      if (value.length === 2) {
        const fullYear = year < 50 ? 2000 + year : 1900 + year;
        return fullYear >= 1900 && fullYear <= currentYear + 1;
      }
      return year >= 1900 && year <= currentYear + 1;
    }),
  
  pucVehMake: Yup.string()
    .trim()
    .required("Vehicle make/model is required")
    .min(2, "Make/model must be at least 2 characters")
    .max(100, "Make/model must be less than 100 characters"),
  
  pucVehLicPlate: Yup.string()
    .trim()
    .required("License plate is required")
    .min(2, "License plate must be at least 2 characters")
    .max(15, "License plate must be less than 15 characters")
    .matches(/^[a-zA-Z0-9\s\-]+$/, "License plate can only contain letters, numbers, spaces, and hyphens")
    .transform(value => value.toUpperCase()),
  
  pucVin: Yup.string()
    .trim()
    .required("VIN is required")
    .min(11, "VIN must be at least 11 characters")
    .max(17, "VIN must be 17 characters or less")
    .matches(/^[A-HJ-NPR-Z0-9]+$/i, "VIN can only contain letters (excluding I, O, Q) and numbers")
    .transform(value => value.toUpperCase()),
  
  // Signature fields
  signatureType: Yup.string().required(),
  signatureText: Yup.string().when('signatureType', {
    is: 'text',
    then: Yup.string()
      .trim()
      .required("Signature is required")
      .min(2, "Signature must be at least 2 characters"),
    otherwise: Yup.string()
  }),
  signatureImage: Yup.string().when('signatureType', {
    is: 'draw',
    then: Yup.string().required("Please draw your signature"),
    otherwise: Yup.string()
  }),
});

// Signature Canvas Component
const SignatureCanvas = ({ onSignatureChange, signatureData }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && signatureData) {
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = signatureData;
    }
  }, [signatureData]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    ctx.beginPath();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onSignatureChange(dataUrl);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange('');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={150}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        style={{
          border: '2px solid #ccc',
          borderRadius: '4px',
          cursor: 'crosshair',
          touchAction: 'none',
          width: '100%',
          maxWidth: '500px',
          height: '150px',
          backgroundColor: '#fff'
        }}
      />
      {hasSignature && (
        <button
          type="button"
          onClick={clearSignature}
          className="btn btn-sm btn-danger"
          style={{ marginTop: '10px' }}
        >
          Clear Signature
        </button>
      )}
    </div>
  );
};

const PucForm = () => {
  const location = useLocation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [signatureType, setSignatureType] = useState('draw');
  const [signatureDate, setSignatureDate] = useState(null);
  const [signatureTime, setSignatureTime] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [downloadLink, setDownloadLink] = useState('');

  // Get data passed from previous page
  const { plateNumber, violationId } = location.state || {};

  // Redirect if no plate number
  useEffect(() => {
    if (!plateNumber) {
      history.push(routes.home.path);
    }
  }, [plateNumber, history]);

  const handleSignatureComplete = (setFieldValue) => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
    const timeStr = now.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    
    setSignatureDate(dateStr);
    setSignatureTime(timeStr);
    setFieldValue('signatureDate', dateStr);
    setFieldValue('signatureTime', timeStr);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      // Sanitize all text inputs
      const sanitizedValues = {
        ...values,
        pucName: values.pucName.trim(),
        pucEmail: values.pucEmail.trim().toLowerCase(),
        pucAddress: values.pucAddress.trim(),
        pucCity: values.pucCity.trim(),
        pucState: values.pucState.trim(),
        pucPhone: values.pucPhone.trim(),
        pucLicNum: values.pucLicNum.trim().toUpperCase(),
        pucVehYear: values.pucVehYear.trim(),
        pucVehMake: values.pucVehMake.trim(),
        pucVehLicPlate: values.pucVehLicPlate.trim().toUpperCase(),
        pucVin: values.pucVin.trim().toUpperCase(),
        signatureText: values.signatureText ? values.signatureText.trim() : '',
        plateNumber,
        violationId,
      };

      // Call API to generate PDF
      const response = await axios.post(
        `${REACT_APP_API_URL}submit-puc-form`,
        sanitizedValues,
        {
          headers: {
            'api-key': REACT_APP_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 && response.data.success) {
        setDownloadLink(response.data.data.download_url || '');
        setSubmitted(true);
        toast.success("Form submitted successfully!");
      } else {
        toast.error(response.data.message || "Failed to submit form. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting PUC form:", error);
      toast.error(error.response?.data?.message || "Failed to submit form. Please try again.");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  if (!plateNumber) {
    return null;
  }

  if (submitted) {
    return (
      <section className="main_section">
        <div className="container">
          <div className="row">
            <div className="col-md-8 col-md-offset-2" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '40px', 
                borderRadius: '8px',
                border: '2px solid #28a745'
              }}>
                <i className="fas fa-check-circle" style={{ fontSize: '64px', color: '#28a745', marginBottom: '20px' }}></i>
                <h2 style={{ color: '#28a745', marginBottom: '20px' }}>Thank You for Your Submission!</h2>
                <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                  Your PUC form has been successfully submitted.
                </p>
                {downloadLink && (
                  <div style={{ marginBottom: '30px' }}>
                    <a 
                      href={downloadLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn yellow_btn"
                      style={{ marginRight: '10px' }}
                    >
                      <i className="fas fa-download"></i> Download Your Form
                    </a>
                  </div>
                )}
                <p style={{ fontSize: '16px', color: '#666', marginBottom: '30px', lineHeight: '1.6' }}>
                  Once your form has been confirmed, you will be able to make your first payment, or you can pay the violation in full now. You can also use the "Contact Us" link.
                </p>
                <div>
                  <a href={routes.contactUs.path} className="btn yellow_btn">
                    Contact Us
                  </a>
                  <a 
                    href={routes.home.path} 
                    className="btn btn-default"
                    style={{ marginLeft: '10px' }}
                  >
                    Return to Home
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div>
      {loading && <ApiLoader />}
      <section className="main_section">
        <div className="container">
          <div className="row">
            <div className="col-md-10 col-md-offset-1">
              <div style={{ marginBottom: '30px' }}>
                <h2>Immobilized Vehicle Release Notice with Payment Owed</h2>
                <p style={{ marginTop: '15px', fontSize: '14px', lineHeight: '1.6' }}>
                  In accordance with § 40-10.1-811(1)(a), C.R.S., a vehicle immobilization company shall immediately release an immobilized vehicle if:
                </p>
                <ol style={{ fontSize: '14px', lineHeight: '1.6', paddingLeft: '20px' }}>
                  <li>the authorized or interested person pays at least sixty dollars ($60.00) of the amount owed to the vehicle immobilization company for the immobilization;</li>
                  <li>the authorized or interested person is not a lienholder or insurance company; and</li>
                  <li>the authorized or interested person completes and signs this form.</li>
                </ol>
                <p style={{ fontSize: '14px', color: '#666', marginTop: '15px' }}>
                  <strong>Plate Number:</strong> {plateNumber}
                </p>
              </div>

              <Formik
                initialValues={{
                  pucName: "",
                  pucEmail: "",
                  pucAddress: "",
                  pucCity: "",
                  pucState: "",
                  pucPhone: "",
                  pucLicNum: "",
                  pucVehYear: "",
                  pucVehMake: "",
                  pucVehLicPlate: "",
                  pucVin: "",
                  signatureType: "draw",
                  signatureText: "",
                  signatureImage: "",
                  signatureDate: "",
                  signatureTime: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, setFieldValue, isSubmitting, errors, touched }) => (
                  <Form>
                    {/* Authorized or Interested Person Information */}
                    <div style={{ 
                      backgroundColor: '#fff', 
                      padding: '20px', 
                      marginBottom: '30px',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}>
                      <h4 style={{ marginTop: '0', marginBottom: '20px', color: '#000' }}>
                        Authorized or Interested Person Information
                      </h4>

                      <div className="form-group">
                        <label>Printed Name *</label>
                        <Field
                          type="text"
                          name="pucName"
                          className="form-control puc-form-black-text"
                          placeholder="Enter your full name"
                        />
                        <ErrorMessage name="pucName" component="div" className="text-danger" />
                      </div>

                      <div className="form-group">
                        <label>Email Address *</label>
                        <Field
                          type="email"
                          name="pucEmail"
                          className="form-control puc-form-black-text"
                          placeholder="your.email@example.com"
                        />
                        <ErrorMessage name="pucEmail" component="div" className="text-danger" />
                      </div>

                      <div className="form-group">
                        <label>Address *</label>
                        <Field
                          type="text"
                          name="pucAddress"
                          className="form-control puc-form-black-text"
                          placeholder="Street address"
                        />
                        <ErrorMessage name="pucAddress" component="div" className="text-danger" />
                      </div>

                      <div className="row">
                        <div className="col-md-5">
                          <div className="form-group">
                            <label>City *</label>
                            <Field
                              type="text"
                              name="pucCity"
                              className="form-control puc-form-black-text"
                              placeholder="City"
                                />
                            <ErrorMessage name="pucCity" component="div" className="text-danger" />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>State *</label>
                            <Field
                              type="text"
                              name="pucState"
                              className="form-control puc-form-black-text"
                              placeholder="State"
                                />
                            <ErrorMessage name="pucState" component="div" className="text-danger" />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group">
                            <label>Telephone Number *</label>
                            <Field
                              type="tel"
                              name="pucPhone"
                              className="form-control puc-form-black-text"
                              placeholder="(555) 123-4567"
                                />
                            <ErrorMessage name="pucPhone" component="div" className="text-danger" />
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Driver's License Number *</label>
                        <Field
                          type="text"
                          name="pucLicNum"
                          className="form-control puc-form-black-text"
                          placeholder="Enter driver's license number"
                        />
                        <ErrorMessage name="pucLicNum" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* Immobilized Vehicle Information */}
                    <div style={{ 
                      backgroundColor: '#fff', 
                      padding: '20px', 
                      marginBottom: '30px',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}>
                      <h4 style={{ marginTop: '0', marginBottom: '20px', color: '#000' }}>
                        Immobilized Vehicle Information
                      </h4>

                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>Year *</label>
                            <Field
                              type="text"
                              name="pucVehYear"
                              className="form-control puc-form-black-text"
                              placeholder="2020"
                              maxLength="4"
                                />
                            <ErrorMessage name="pucVehYear" component="div" className="text-danger" />
                          </div>
                        </div>
                        <div className="col-md-9">
                          <div className="form-group">
                            <label>Make/Model *</label>
                            <Field
                              type="text"
                              name="pucVehMake"
                              className="form-control puc-form-black-text"
                              placeholder="e.g., Toyota Camry"
                                />
                            <ErrorMessage name="pucVehMake" component="div" className="text-danger" />
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>License Plate *</label>
                            <Field
                              type="text"
                              name="pucVehLicPlate"
                              className="form-control puc-form-black-text"
                              placeholder="ABC-1234"
                              style={{ textTransform: 'uppercase', color: '#000 !important' }}
                            />
                            <ErrorMessage name="pucVehLicPlate" component="div" className="text-danger" />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>VIN *</label>
                            <Field
                              type="text"
                              name="pucVin"
                              className="form-control puc-form-black-text"
                              placeholder="Up To 17 Letters or Numbers ONLY of Full VIN"
                              maxLength="17"
                              style={{ textTransform: 'uppercase', color: '#000 !important' }}
                            />
                            <ErrorMessage name="pucVin" component="div" className="text-danger" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Signature Section */}
                    <div style={{ 
                      backgroundColor: '#fff', 
                      padding: '20px', 
                      marginBottom: '30px',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}>
                      <h4 style={{ marginTop: '0', marginBottom: '20px', color: '#000' }}>
                        Signature of Agreement *
                      </h4>

                      <div style={{ marginBottom: '20px' }}>
                        {signatureType === 'draw' ? (
                          <button
                            type="button"
                            onClick={() => {
                              setSignatureType('text');
                              setFieldValue('signatureType', 'text');
                              setFieldValue('signatureImage', '');
                            }}
                            className="btn btn-link"
                            style={{ padding: '0', textDecoration: 'underline' }}
                          >
                            Enter Signature as plain text
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setSignatureType('draw');
                              setFieldValue('signatureType', 'draw');
                              setFieldValue('signatureText', '');
                            }}
                            className="btn btn-link"
                            style={{ padding: '0', textDecoration: 'underline' }}
                          >
                            Draw Signature instead
                          </button>
                        )}
                      </div>

                      {signatureType === 'draw' ? (
                        <div className="form-group">
                          <label>Draw Your Signature *</label>
                          <SignatureCanvas
                            onSignatureChange={(dataUrl) => {
                              setFieldValue('signatureImage', dataUrl);
                              if (dataUrl && !signatureDate) {
                                handleSignatureComplete(setFieldValue);
                              }
                            }}
                            signatureData={values.signatureImage}
                          />
                          <ErrorMessage name="signatureImage" component="div" className="text-danger" />
                          <small className="form-text text-muted">
                            Sign above using your mouse or finger
                          </small>
                        </div>
                      ) : (
                        <div className="form-group">
                          <label>Type Your Signature *</label>
                          <Field
                            type="text"
                            name="signatureText"
                            className="form-control puc-form-black-text"
                            placeholder="Type your full name to sign"
                            style={{ fontFamily: 'cursive', fontSize: '24px' }}
                            onBlur={(e) => {
                              if (e.target.value && !signatureDate) {
                                handleSignatureComplete(setFieldValue);
                              }
                            }}
                          />
                          <ErrorMessage name="signatureText" component="div" className="text-danger" />
                        </div>
                      )}

                      {signatureDate && signatureTime && (
                        <div className="row" style={{ marginTop: '20px' }}>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Date</label>
                              <input
                                type="text"
                                className="form-control puc-form-black-text"
                                value={signatureDate}
                                readOnly
                                style={{ backgroundColor: '#e9ecef' }}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Time</label>
                              <input
                                type="text"
                                className="form-control puc-form-black-text"
                                value={signatureTime}
                                readOnly
                                style={{ backgroundColor: '#e9ecef' }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="form-group" style={{ textAlign: 'center', marginTop: '30px' }}>
                      <button
                        type="submit"
                        className="btn yellow_btn"
                        disabled={isSubmitting || loading}
                        style={{ minWidth: '200px', fontSize: '18px', padding: '12px 30px' }}
                      >
                        {isSubmitting || loading ? 'Submitting...' : 'Submit Form'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PucForm;
